/**
 * Real-Time Notifications Hook
 *
 * Subscribes to Supabase Realtime for notification table changes and
 * invalidates the matching TanStack Query caches. Thin wrapper around the
 * canonical {@link useRealtimeTable} hook (the Phase 2 consolidation point).
 *
 * Usage:
 * ```tsx
 * useRealtimeNotifications({
 *   onNotificationUpdate: async () => { // refresh context },
 * });
 * ```
 *
 * **Migration notes (Phase 2d):**
 * The previous implementation opened its own `supabase.channel()`, manually
 * spliced the React Query cache on every event type, fired push notifications
 * on INSERT, and conditionally invalidated task/comment queries. The new
 * implementation delegates the subscription to `useRealtimeTable` and keeps
 * the domain-specific side effects (push notifications, context refresh,
 * cross-domain invalidation) in the `onChange` callback.
 *
 * @module useRealtimeNotifications
 */

import { useAuth } from "@/hooks/useAuth";
import { useRealtimeTable } from "@/hooks/useRealtimeTable";
import type { Notification } from "@/types/notification";
import type { RealtimePayload } from "@/types/realtime";
import { pushNotificationService } from "@/services/integrations/push-notification.service";
import { showNotificationToast } from "@/utils/notification-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { logger } from "@/lib/logger";

interface UseRealtimeNotificationsProps {
  onNotificationUpdate?: () => Promise<void>;
}

export const useRealtimeNotifications = ({
  onNotificationUpdate,
}: UseRealtimeNotificationsProps = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleChange = useCallback(
    async (payload: RealtimePayload<Notification>) => {
      logger.info("🔔 Real-time notification update received", {
        eventType: payload.eventType,
        payload,
      });

      // Trigger external notification context refresh for immediate toast
      // display. This is a valid UI reflex — the context is read-only and
      // only drives the notification bell/drawer.
      if (onNotificationUpdate) {
        logger.info(
          "🔄 Triggering external notification context refresh for immediate toast display",
        );
        await onNotificationUpdate();
      }

      // ----------------------------------------------------------------
      // INSERT-specific side effects
      // ----------------------------------------------------------------
      if (payload.eventType === "INSERT" && payload.new) {
        const newNotification = payload.new;
        logger.info("➕ New notification inserted", {
          type: newNotification.type,
          id: newNotification.id,
        });

        // Show an in-app toast for the new notification.
        // Sonner's `id` (derived from the notification ID) prevents stacking.
        showNotificationToast(newNotification, navigate);

        // Trigger push notification (browser/PWA notification).
        try {
          const notificationData =
            typeof newNotification.data === "object" &&
            newNotification.data !== null
              ? (newNotification.data as Record<string, unknown>)
              : {};
          const pushPayload: Record<string, unknown> = {
            ...notificationData,
            notification_id: newNotification.id,
          };

          if (newNotification.message_preview) {
            pushPayload.message_preview = newNotification.message_preview;
          }

          const pushRes =
            await pushNotificationService.sendNotificationForType(
              newNotification.type,
              pushPayload,
            );
          if (!pushRes.success) {
            logger.warn("Push notification skipped", {
              type: newNotification.type,
              error: pushRes.error,
            });
          } else {
            logger.info("📱 Push notification triggered", {
              type: newNotification.type,
            });
          }
        } catch (pushError) {
          logger.warn("Failed to trigger push notification", {
            error: pushError,
          });
        }

        // Cross-domain invalidation: task/comment-related notifications
        // should refresh the corresponding caches so the UI stays in sync.
        if (
          newNotification.entity_type === "task" ||
          newNotification.type.includes("TASK")
        ) {
          logger.info(
            "🔄 Invalidating task queries due to task-related notification",
          );
          queryClient.invalidateQueries({ queryKey: ["todos"] });
          queryClient.invalidateQueries({ queryKey: ["tasks"] });
        }

        if (
          newNotification.entity_type === "task_comment" ||
          newNotification.entity_type === "comment"
        ) {
          logger.info(
            "🔄 Invalidating comment queries due to comment-related notification",
          );
          queryClient.invalidateQueries({ queryKey: ["task-comments"] });
          queryClient.invalidateQueries({ queryKey: ["comments"] });
        }
      }
    },
    [onNotificationUpdate, queryClient, navigate],
  );

  const { isSubscribed } = useRealtimeTable("notifications", {
    enabled: Boolean(user?.id),
    filter: user?.id
      ? { column: "user_id", operator: "eq" as const, value: user.id }
      : undefined,
    invalidateKeys: [
      ["notifications"],
      ["notifications", user?.id],
      ["notification-count", user?.id],
    ],
    onChange: handleChange,
  });

  return { isSubscribed };
}; 